import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    updateProfile,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebaseConfig';
import { signInWithGoogle as signInWithGoogleService } from '../services/googleAuthService';
import { APP_VERSION } from '../version';
import storage from '../services/storageService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Login com Email/Senha
    const login = async (email, password) => {
        setError(null);
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            return result.user;
        } catch (err) {
            const errorMessage = getFirebaseErrorMessage(err.code);
            setError(errorMessage);
            return null; // Retorna null em vez de lançar exceção
        }
    };

    // Cadastro com Email/Senha
    const register = async (email, password, displayName, couponCode) => {
        setError(null);
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            // Atualiza o perfil com o nome
            if (displayName) {
                await updateProfile(result.user, { displayName });
            }

            // Salva cupom pendente no storage para que o App.js possa aplicar ao inicializar o Firestore
            if (couponCode) {
                storage.setItem('pending_coupon', couponCode);
                console.log('AUTH_CONTEXT: Cupom salvo para aplicação posterior.');
            }

            return result.user;
        } catch (err) {
            storage.removeItem('pending_coupon'); // Limpa se falhar o cadastro
            const errorMessage = getFirebaseErrorMessage(err.code);
            setError(errorMessage);
            return null; // Retorna null em vez de lançar exceção
        }
    };

    // Login com Google (Nativo ou Web)
    const loginWithGoogle = async (couponCode, onProgress) => {
        setError(null);
        try {
            // Salva cupom pendente no storage
            if (couponCode) {
                storage.setItem('pending_coupon', couponCode);
            }

            // Usa serviço unificado que detecta plataforma
            const loggedUser = await signInWithGoogleService(onProgress);

            if (loggedUser) {
                // Atualiza o estado imediatamente para evitar delay perceptível
                setUser(loggedUser);
            }

            return loggedUser;
        } catch (err) {
            storage.removeItem('pending_coupon'); // Limpa se falhar o login
            // Se o erro vier do plugin/firebase, tenta traduzir
            const errorCode = err.code || err.message;
            const errorMessage = getFirebaseErrorMessage(errorCode);
            setError(errorMessage);
            return null; // Retorna null em vez de lançar exceção
        }
    };



    // Logout
    const logout = async () => {
        try {
            await signOut(auth);
        } catch (err) {
            console.error('Erro ao fazer logout:', err);
        }
    };

    // Recuperação de Senha
    const resetPassword = async (email) => {
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
            return true;
        } catch (err) {
            const errorMessage = getFirebaseErrorMessage(err.code);
            setError(errorMessage);
            return false; // Retorna false em vez de lançar exceção
        }
    };

    // Traduz erros do Firebase para português
    const getFirebaseErrorMessage = (code) => {
        const messages = {
            'auth/email-already-in-use': 'Este email já está cadastrado.',
            'auth/credential-already-in-use': 'Esta conta já está vinculada a outro usuário.',
            'auth/invalid-email': 'Email inválido.',
            'auth/operation-not-allowed': 'Operação não permitida.',
            'auth/weak-password': 'Senha muito fraca. Use pelo menos 6 caracteres.',
            'auth/user-disabled': 'Esta conta foi desativada.',
            'auth/user-not-found': 'Usuário não encontrado.',
            'auth/wrong-password': 'Senha incorreta.',
            'auth/invalid-credential': 'Credenciais inválidas. Verifique email e senha.',
            'auth/too-many-requests': 'Muitas tentativas. Aguarde um momento.',
            'auth/popup-closed-by-user': 'Login cancelado.',
            'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
            'auth/requires-recent-login': 'Para esta operação, faça login novamente.'
        };
        return messages[code] || `Erro ao autenticar. (${code})`;
    };

    const value = {
        user,
        loading,
        error,
        login,
        register,
        loginWithGoogle,

        logout,
        resetPassword,
        clearError: () => setError(null)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
